import { ArrowLeft, ArrowRight, Printer } from "lucide-react";
import SEO from "@/components/SEO";
import { useHydratedSearch } from "@/hooks/useHydratedSearch";
import { buildOrganizationJourneyUrl } from "@/utils/organizationJourney";
import {
  AI_LEADERS_INCLUSIONS,
  AI_LEADERS_PROGRAM,
  AI_LEADERS_PROPOSAL_HREF,
} from "@/data/aiLeadersProgram";
import "./AIForLeadersSummary.css";

const AIForLeadersSummary = () => {
  const search = useHydratedSearch();
  const program = AI_LEADERS_PROGRAM;
  const seatPrice = program.seatPrice.toLocaleString("en-US");
  const programPath = buildOrganizationJourneyUrl("/ai-30", search, { source: "ai-30-summary", location: "program", audience: "organization" });
  const scopePath = buildOrganizationJourneyUrl("/ai-call", search, { source: "ai-30-summary", location: "scope_review", audience: "organization" });

  return (
    <main className="leaders-summary">
      <SEO
        title="AI 30 Team Program Summary | Technical Leaders"
        description="A 30-day AI program for company teams and member organizations. Eight live training hours, guided workflow building, an adoption plan, and implementation office hours. $2,500 per seat base price, with volume discounts quoted by cohort size."
        robots="noindex, follow"
      />

      <nav className="leaders-summary-toolbar" aria-label="Program summary actions">
        <a href={programPath}><ArrowLeft size={18} aria-hidden="true" /> Back to the program</a>
        <button type="button" onClick={() => window.print()}>
          <Printer size={18} aria-hidden="true" /> Print or save as PDF
        </button>
      </nav>

      <article className="leaders-summary-sheet" aria-labelledby="leaders-summary-title">
        <header className="leaders-summary-heading">
          <p className="leaders-summary-brand">Tech Leaders <span>× Bottega8</span></p>
          <p className="leaders-summary-eyebrow">For company teams &amp; member organizations · 30-day program</p>
          <h1 id="leaders-summary-title">{program.name}</h1>
          <p className="leaders-summary-objective">{program.headline}</p>
          <p>{program.commitment}</p>
        </header>

        <dl className="leaders-summary-facts">
          <div><dt>Base price</dt><dd>${seatPrice} per seat</dd></div>
          <div><dt>Cohort size</dt><dd>Seats agreed in proposal</dd></div>
          <div><dt>Live training</dt><dd>{program.liveHours} hours</dd></div>
          <div><dt>Program & support</dt><dd>{program.durationDays} days</dd></div>
        </dl>
        <p>{program.pricingNote} We confirm the seat count and total investment in your team proposal.</p>

        <section className="leaders-summary-inclusions" aria-labelledby="leaders-summary-inclusions-title">
          <h2 id="leaders-summary-inclusions-title">What&apos;s included</h2>
          <ul>
            {AI_LEADERS_INCLUSIONS.map((item) => (
              <li key={item.title}><h3>{item.title}</h3><p>{item.description}</p></li>
            ))}
          </ul>
        </section>

        <div className="leaders-summary-preparation">
          <section aria-labelledby="leaders-summary-participation-title">
            <h2 id="leaders-summary-participation-title">What we need from your team</h2>
            <p>Eight live hours, on-site or virtual, with implementation support throughout the 30-day program. Bring a sponsor, approved tools and sources, a reviewer, and time to practice between sessions. No coding required.</p>
          </section>
          <section aria-labelledby="leaders-summary-focus-title">
            <h2 id="leaders-summary-focus-title">What your team keeps</h2>
            <p>A reusable brief, a reviewed workflow, a comparable result with time, cost, and quality measured against a baseline, and an adoption plan. Your organization owns rollout. Additional implementation by our team is scoped and priced separately.</p>
          </section>
        </div>

        <footer className="leaders-summary-next-step">
          <div>
            <h2>Let&apos;s talk about your team</h2>
            <p>Organizing a company team or industry group? Start with a 15-minute call about your people, the work you want to improve, format, and timing.</p>
            <a className="leaders-summary-contact" href={AI_LEADERS_PROPOSAL_HREF}>Request a team proposal by email</a>
          </div>
          <a className="leaders-summary-proposal" href={scopePath}>Book a call with our team <ArrowRight size={18} aria-hidden="true" /></a>
          <p className="leaders-summary-full-link">Full team program: <a href="https://www.technical-leaders.com/ai-30">technical-leaders.com/ai-30</a></p>
        </footer>
      </article>
    </main>
  );
};

export default AIForLeadersSummary;
